import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Stack,
  Tooltip,
  Chip,
  Paper,
  Grid,
  useMediaQuery,
  useTheme
} from "@mui/material";
import SlickSlider from "react-slick";
import { useGetStageBookings } from "../../../../services/stagemode_api/venue/useGetStageBookings";
import { useAuth } from "../../../../hooks";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { useNavigate } from "react-router-dom";
// import BookingCard from "../BookedCardDesign";
import motion from "framer-motion";
dayjs.extend(duration);

export interface StageBooking {
  id: number;
  stage_card_id: number;
  talent_id: number;
  user_id: number;
  channel_name: string;
  venue_name: string;
  venue_size: string;
  venue_location: string;
  message: string;
  contact_email: string;
  start_date_time: string;
  end_date_time: string;
  payment_status: "Paid" | "Unpaid" | "Failed";
  status: "Ongoing" | "Pending" | "Completed" | "Cancelled" | "Scheduled";
  first_reminder_sent?: number;
  talent?: {
    username?: string;
  };
  stagecard?: {
    title?: string;
    short_description?: string;
    primary_thumbnail?: string;
    banner_title?: string;
    type?: string;
  };
}

const Slider: any = SlickSlider;

const statusLabels: Record<string, string> = {
  Ongoing: "Ongoing StageCard Sessions",
  Pending: "Pending StageCard Sessions",
  Scheduled: "Scheduled StageCard Sessions",
  Completed: "Completed StageCard Sessions",
  Cancelled: "Cancelled StageCard Sessions",
};

const statusOrder: StageBooking["status"][] = [
  "Ongoing",
  "Pending",
  "Scheduled",
  "Completed",
  "Cancelled",
];

// —— Netflix-ish tokens
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  border: "rgba(255,255,255,0.12)",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.75)",
  red: "#E50914",
  redHover: "#b20710",
  soft: "rgba(255,255,255,0.06)",
};

// ⏱ Countdown logic
const useCountdown = (targetTime: string) => {
  const [remaining, setRemaining] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = dayjs();
      const target = dayjs(targetTime);
      const diff = target.diff(now, "second");

      if (diff <= 0) {
        setRemaining(null);
        setIsReady(true);
        clearInterval(interval);
      } else {
        const min = Math.floor(diff / 60);
        const sec = diff % 60;
        setRemaining(`${min}m ${sec < 10 ? `0${sec}` : sec}s`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [targetTime]);

  return { remaining, isReady };
};

const BookingCard: React.FC<{ booking: StageBooking }> = ({ booking }) => {
  const navigate = useNavigate();
  const { remaining } = useCountdown(booking.start_date_time);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const sessionStart = dayjs(booking.start_date_time);
  const sessionEnd = dayjs(booking.end_date_time);
  const now = dayjs();

  const isJoinable =
    booking.status === "Ongoing" ||
    (now.isAfter(sessionStart.subtract(1, "minute")) &&
      now.isBefore(sessionEnd.add(5, "minute")));

  const statusStyles = (() => {
    switch (booking.status) {
      case "Scheduled":
        return { chipBg: "rgba(229,9,20,0.15)", chipColor: COLORS.red };
      case "Pending":
        return { chipBg: COLORS.soft, chipColor: COLORS.text };
      case "Ongoing":
        return { chipBg: "rgba(16,185,129,0.15)", chipColor: "#10B981" };
      case "Completed":
        return { chipBg: "rgba(59,130,246,0.15)", chipColor: "#3B82F6" };
      case "Cancelled":
        return { chipBg: "rgba(255,255,255,0.08)", chipColor: COLORS.textMuted };
      default:
        return { chipBg: COLORS.soft, chipColor: COLORS.textMuted };
    }
  })();

  return (
    <Box px={{ xs: 1, sm: 2 }} width="100%" maxWidth={480}>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${COLORS.border}`,
          borderRadius: 3,
          p: { xs: 2, sm: 3 },
          background: COLORS.panel,
          color: COLORS.text,
          transition: "transform 200ms ease, box-shadow 200ms ease",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
          overflow: "hidden",
          minHeight: { xs: 200, sm: 220 },
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(800px 380px at 10% 0%, rgba(229,9,20,0.08), transparent 60%)",
            pointerEvents: "none",
          },
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 18px 48px rgba(0,0,0,0.45)",
          },
        }}
      >
        <Box>
          <Typography
            fontWeight={900}
            fontSize={{ xs: 15, sm: 17 }}
            noWrap
            title={booking.stagecard?.title}
            sx={{
              letterSpacing: "-0.01em",
              color: COLORS.text,
              mb: 0.5,
            }}
          >
            {booking.stagecard?.title || "Untitled Event"}
          </Typography>

          <Typography fontSize={{ xs: 12.5, sm: 13.5 }} sx={{ color: COLORS.textMuted }}>
            📍 {booking.venue_name}, {booking.venue_location}
          </Typography>

          <Typography fontSize={{ xs: 12.5, sm: 13.5 }} sx={{ color: COLORS.textMuted, mt: 0.25 }}>
            🕒 {sessionStart.format("MMM D, YYYY")} — {sessionStart.format("h:mm A")} – {sessionEnd.format("h:mm A")}
          </Typography>

          <Typography
            mt={1}
            fontSize={{ xs: 12.5, sm: 13.5 }}
            sx={{
              color: COLORS.textMuted,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {booking.message || "No message provided."}
          </Typography>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 1.5 }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          mt={2.25}
        >
          <Chip
            label={booking.status}
            size="small"
            sx={{
              fontWeight: 800,
              fontSize: 12,
              px: 1.25,
              height: 26,
              borderRadius: 2,
              bgcolor: statusStyles.chipBg,
              color: statusStyles.chipColor,
              border: `1px solid ${COLORS.border}`,
            }}
          />

          <Box sx={{ flexGrow: 1 }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            width={{ xs: "100%", sm: "auto" }}
          >
            <Button
              variant="outlined"
              fullWidth={isMobile}
              size="small"
              sx={{
                fontFamily: "Lexend",
                textTransform: "none",
                borderRadius: 2,
                fontSize: 13,
                minWidth: 96,
                color: COLORS.text,
                borderColor: COLORS.border,
                bgcolor: "transparent",
                "&:hover": {
                  borderColor: COLORS.text,
                  bgcolor: COLORS.soft,
                },
              }}
              onClick={() =>
                navigate(`/stage-booking/${booking.id}`, {
                  state: { booking },
                })
              }
            >
              View
            </Button>

            {/* Join button was commented in your original—kept feature parity by leaving it out.
                If you want it back, just uncomment and it will match the Netflix look. */}
            {/* {booking.status === "Scheduled" && (
              <Button
                variant="contained"
                fullWidth={isMobile}
                size="small"
                disabled={!isJoinable}
                sx={{
                  fontFamily: "Lexend",
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: 13,
                  px: 2,
                  borderRadius: 2,
                  minWidth: 110,
                  color: isJoinable ? "#fff" : COLORS.textMuted,
                  background: isJoinable
                    ? `linear-gradient(180deg, ${COLORS.red}, ${COLORS.redHover})`
                    : COLORS.soft,
                  border: `1px solid ${
                    isJoinable ? "rgba(229,9,20,0.6)" : COLORS.border
                  }`,
                  boxShadow: isJoinable ? "0 12px 28px rgba(229,9,20,0.35)" : "none",
                  "&:hover": {
                    background: isJoinable
                      ? `linear-gradient(180deg, ${COLORS.redHover}, ${COLORS.red})`
                      : COLORS.soft,
                  },
                }}
                onClick={() =>
                  navigate(`/stage-live-host/${booking.channel_name}?bookingId=${booking.id}`)
                }
              >
                {isJoinable ? "Join Now" : remaining ?? "Starting..."}
              </Button>
            )} */}
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};

const BookStageCardCarousel: React.FC = () => {
  const { userInfo } = useAuth();
  const { data, isLoading } = useGetStageBookings({
    key: ["stage-bookings", userInfo?.id],
  });

  const allBookings: StageBooking[] = data?.data ?? [];

  const grouped: Record<string, StageBooking[]> = {};

  allBookings.forEach((booking) => {
    const status = booking.status || "Unknown";
    if (!grouped[status]) grouped[status] = [];
    grouped[status].push(booking);
  });

  const hasBookings = statusOrder.some((status) => grouped[status]?.length > 0);

  if (isLoading) {
    return (
      <Box py={4} display="flex" justifyContent="center" sx={{ backgroundColor: COLORS.bg }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!hasBookings) {
    return (
      <Box py={6} textAlign="center" sx={{ backgroundColor: COLORS.bg, borderRadius: 2 }}>
        <Typography variant="h6" fontFamily="Lexend" sx={{ color: COLORS.textMuted }}>
          You haven't received any StageMode bookings yet.
        </Typography>
        <Typography fontSize={14} mt={1} fontFamily="Lexend" sx={{ color: COLORS.textMuted }}>
          Once users book your stage cards, they'll appear here grouped by status.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: COLORS.bg, px: { xs: 1, sm: 2 }, py: { xs: 2, sm: 3 }, borderRadius: 2 }}>
      {statusOrder.map((status) => {
        const bookings = grouped[status];
        if (!bookings || bookings.length === 0) return null;

        return (
          <Box key={status} mb={6}>
            <Typography
              variant="h6"
              fontWeight={900}
              fontFamily="Lexend"
              sx={{
                color: COLORS.text,
                mb: 2,
                letterSpacing: "-0.02em",
                fontSize: { xs: 16, sm: 18, md: 20 },
                textShadow: "0 1px 0 rgba(0,0,0,0.35)",
              }}
            >
              {statusLabels[status]}
            </Typography>

            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              {bookings.map((booking) => (
                <Grid key={booking.id} item xs={12} sm={6} md={4}>
                  <BookingCard booking={booking} />
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
};

export default BookStageCardCarousel;
